import { Bot, RotateCcw } from "lucide-react";
import type { LlmModelProvider } from "@/types/organization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import { CUSTOM_MODEL_SELECT_VALUE } from "@/components/modals/organizationSettings/constants";
import { getCustomLlmModelError } from "@/components/modals/organizationSettings/helpers";
import type { LlmModelSettings } from "@/components/modals/organizationSettings/hooks/useLlmModelSettings";

interface Props {
  llmModelSettings: LlmModelSettings;
  onCancel: () => void;
}

export const AiModelsTab = ({ llmModelSettings, onCancel }: Props) => {
  const {
    llmModelConfig,
    isLoadingLlmModelConfig,
    isUpdatingLlmModelConfig,
    setSelectedLlmModels,
    setSelectedLlmModelProviders,
    getSelectedLlmModel,
    getKnownLlmModelOption,
    getSelectedLlmProvider,
    isCustomLlmModel,
    invalidLlmModelProcess,
    onLlmModelSubmit,
  } = llmModelSettings;

  return (
    <div className="mt-6 space-y-6">
      {isLoadingLlmModelConfig ? (
        <div className="flex h-40 items-center justify-center">
          <Loader className="h-6 w-6 text-gray-400" />
        </div>
      ) : !llmModelConfig ? (
        <p className="text-sm text-gray-500">Unable to load AI model settings</p>
      ) : (
        <>
          <div className="space-y-4">
            {llmModelConfig.processes.map(process => {
              const selectedModel = getSelectedLlmModel(process);
              const selectedProvider = getSelectedLlmProvider(process, selectedModel);
              const providerOptions = Array.from(new Set(process.availableModels.map(model => model.provider)));
              const modelOptions = process.availableModels.filter(model => model.provider === selectedProvider);
              const isCustomModel = isCustomLlmModel(process, selectedModel);
              const customModelError = isCustomModel ? getCustomLlmModelError(selectedModel) : null;
              const isDefault = selectedModel === process.defaultModel;
              const defaultModelOption = getKnownLlmModelOption(process, process.defaultModel);
              return (
                <div key={process.process} className="space-y-3 rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 gap-3">
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-600">
                        <Bot size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <Label className="text-sm font-semibold">{process.label}</Label>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            {isDefault ? "Default" : "Override"}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{process.description}</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedLlmModels(prev => ({
                          ...prev,
                          [process.process]: process.defaultModel,
                        }));
                        if (defaultModelOption) {
                          setSelectedLlmModelProviders(prev => ({
                            ...prev,
                            [process.process]: defaultModelOption.provider,
                          }));
                        }
                      }}
                      disabled={isUpdatingLlmModelConfig || isDefault}
                    >
                      <RotateCcw size={14} className="mr-2" />
                      Reset
                    </Button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-[180px_1fr]">
                    <div className="space-y-2">
                      <Label htmlFor={`provider-${process.process}`} className="text-sm font-normal text-gray-500">
                        Provider
                      </Label>
                      <select
                        id={`provider-${process.process}`}
                        value={selectedProvider}
                        onChange={event => {
                          const provider = event.target.value as LlmModelProvider;
                          setSelectedLlmModelProviders(prev => ({
                            ...prev,
                            [process.process]: provider,
                          }));
                          if (!isCustomModel) {
                            const defaultModelForProvider = process.availableModels.find(
                              option => option.provider === provider && option.model === process.defaultModel,
                            );
                            const firstModelForProvider = process.availableModels.find(
                              option => option.provider === provider,
                            );
                            setSelectedLlmModels(prev => ({
                              ...prev,
                              [process.process]:
                                defaultModelForProvider?.model ?? firstModelForProvider?.model ?? process.defaultModel,
                            }));
                          }
                        }}
                        disabled={isUpdatingLlmModelConfig || providerOptions.length <= 1}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm capitalize focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        {providerOptions.map(provider => (
                          <option key={provider} value={provider}>
                            {provider}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`model-${process.process}`} className="text-sm font-normal text-gray-500">
                        Model
                      </Label>
                      <select
                        id={`model-${process.process}`}
                        value={isCustomModel ? CUSTOM_MODEL_SELECT_VALUE : selectedModel}
                        onChange={event => {
                          const model = event.target.value;
                          if (model === CUSTOM_MODEL_SELECT_VALUE) {
                            setSelectedLlmModelProviders(prev => ({
                              ...prev,
                              [process.process]: selectedProvider,
                            }));
                            setSelectedLlmModels(prev => ({
                              ...prev,
                              [process.process]: isCustomModel ? selectedModel : "",
                            }));
                            return;
                          }
                          const selectedOption = process.availableModels.find(option => option.model === model);
                          setSelectedLlmModels(prev => ({
                            ...prev,
                            [process.process]: model,
                          }));
                          if (selectedOption) {
                            setSelectedLlmModelProviders(prev => ({
                              ...prev,
                              [process.process]: selectedOption.provider,
                            }));
                          }
                        }}
                        disabled={isUpdatingLlmModelConfig}
                        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        {modelOptions.map(model => (
                          <option key={model.model} value={model.model}>
                            {model.label}
                          </option>
                        ))}
                        <option value={CUSTOM_MODEL_SELECT_VALUE}>Custom model...</option>
                      </select>
                    </div>
                    {isCustomModel && (
                      <div className="space-y-2 md:col-start-2">
                        <Label
                          htmlFor={`custom-model-${process.process}`}
                          className="text-sm font-normal text-gray-500"
                        >
                          Custom model name
                        </Label>
                        <Input
                          id={`custom-model-${process.process}`}
                          value={selectedModel}
                          onChange={event =>
                            setSelectedLlmModels(prev => ({
                              ...prev,
                              [process.process]: event.target.value,
                            }))
                          }
                          disabled={isUpdatingLlmModelConfig}
                          placeholder="gpt-custom-2026-07-10"
                          className={customModelError ? "border-red-500 focus-visible:ring-red-500" : ""}
                        />
                        {customModelError && <p className="text-xs text-red-500">{customModelError}</p>}
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-gray-500">Default model: {process.defaultModel}</p>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 border-t pt-6">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isUpdatingLlmModelConfig}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isUpdatingLlmModelConfig || invalidLlmModelProcess !== undefined}
              onClick={onLlmModelSubmit}
            >
              {isUpdatingLlmModelConfig ? "Saving..." : "Save AI Models"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
